import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { RouterConfig } from '../../../../core/utils/constants';
import { Translate } from 'react-localize-redux';
import { translate, formatDate, lastModified, serviceIcon } from 'shared/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faUsers } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import styles from './GroupOverview.module.scss';
import { GroupData, GroupOverviewProps, Source } from 'secure-pages/groups/Groups.types';
import { getDisplayAppCode } from 'core/constants';
import { useSelector } from 'react-redux';
import { applyGroupFilterToUserTable } from '../../../../shared/helpers/userTableFilters';

export const GroupOverview = (props: GroupOverviewProps): ReactElement => {
  const { groupId, groupData: propsGroupData, loading: propsLoading } = props;
  const history = useHistory();
  const profileId = useSelector((state: any) => state.profile?.data?.id);

  if (propsLoading || !propsGroupData) {
    return <div>{translate('accounts.details.scim.groups.loading') as string}</div>;
  }

  const groupData: GroupData = {
    id: propsGroupData.id,
    name: propsGroupData.source === Source.PLATFORM ? (translate(`groups.${propsGroupData.name}`) as string) : propsGroupData.name,
    groupId: propsGroupData.id,
    source: propsGroupData.source === Source.PLATFORM ? (translate('accounts.details.scim.groups.digicert') as string) : (translate('accounts.details.scim.groups.idProvider') as string),
    lastSync: propsGroupData.updated_at ? lastModified(propsGroupData.updated_at) : 'Never',
    lastModified: propsGroupData.updated_at || propsGroupData.created_at,
    modifiedBy: propsGroupData.updated_by || propsGroupData.created_by || 'Unknown',
    createdAt: propsGroupData.created_at,
    createdBy: propsGroupData.created_by || 'Unknown',
    numberOfUsers: propsGroupData.user_count || 0,
    services: (propsGroupData.applications || []).map((app: any) => ({
      id: app.id,
      name: app.app_name || app.name,
      roles: propsGroupData.roles?.filter((role: any) => role.application_id === app.id).length || 0,
      icon: getDisplayAppCode(app.short_app_code) || app.icon
    }))
  };

  const navigateToUserList = (groupId: string, groupName: string) => {
    applyGroupFilterToUserTable(profileId, groupId, groupName);
    history.push(RouterConfig.urlMap.administrators);
  };

  const navigateToGroupAccess = () => {
    history.push(RouterConfig.groups.groupAccess(groupId));
  }

  return (
    <section className={styles.groupsOverview}>
      <header className={styles.header}>
        <div id='group-details' className={styles.groupInfo}>
          <h1 className={styles.heading}>{groupData.name}</h1>
          <p className={styles.id}><Translate id='common.groupId' />: {groupData.groupId}</p>
          <p className={styles.details}>
            <Translate id='common.createdOn' /> {lastModified(groupData.createdAt)}&nbsp;
            <Translate id='common.at' /> {formatDate(groupData.createdAt, 'h:mm A')}&nbsp;
            <Translate id='common.by' />&nbsp;
            {groupData.createdBy}
            <span className={styles.separator}>|</span>
            {translate('common.lastSyncUpdated') as string} {groupData.lastSync}
          </p>
        </div>
      </header>

      <div className={styles.groupOverviewOptions}>
        <button className={styles.groupOverviewOption}>
          <p className={styles.label}><Translate id='common.groupDetails' /></p>
          <div className={styles.items}>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faUsers as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='accounts.details.scim.groups.group' /></p>
                <p className={styles.name}>{groupData.name}</p>
              </div>
            </div>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faEnvelope as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='common.form.source' /></p>
                <p className={styles.name}>{groupData.source}</p>
              </div>
            </div>
          </div>
        </button>

        <button
          className={styles.groupServicesOption}
          onClick={propsGroupData.source !== Source.PLATFORM  ? navigateToGroupAccess : undefined}
        >
          <p className={styles.label}><Translate id='common.groupAccess' /></p>
          <div className={styles.items}>
            {groupData.services.map((service) => (
              <div className={styles.item} key={service.id}>
                <div className={styles.icon}>{serviceIcon(service.name)}</div>
                <div className={styles.info}>
                  <p className={styles.name}>
                    {service.name} ({service.roles} {service.roles === 1 ? translate('accounts.details.scim.groups.roleUppercase') as string : translate('accounts.details.scim.groups.rolesUppercase') as string})
                  </p>
                </div>
              </div>
            ))}
          </div>
          {groupData.services.length === 0 && propsGroupData.source === Source.PLATFORM && (
            <div className={styles.noServices}>
              {translate('accounts.details.scim.groups.platformGroupsSystemManaged') as string}
            </div>
          )}
          {groupData.services.length === 0 && propsGroupData.source !== Source.PLATFORM && (
            <div className={styles.noServices}>
              {translate('accounts.details.scim.groups.noServicesAssignedToGroup') as string}
            </div>
          )}
        </button>

        <button
          className={styles.groupOverviewOption}
          onClick={() => {
            if (groupData.numberOfUsers > 0) {
              navigateToUserList(groupData.id, groupData.name);
            }
          }}
        >
          <p className={styles.label}><Translate id='common.users' /></p>
          <div className={styles.items}>
            <div className={styles.item}>
              <FontAwesomeIcon icon={faUser as IconProp} className={styles.icon} />
              <div className={styles.info}>
                <p className={styles.label}><Translate id='common.numberOfUsers' /></p>
                <p className={styles.name}>{groupData.numberOfUsers}</p>
              </div>
            </div>
          </div>
        </button>
      </div>
    </section>
  );
};

export default GroupOverview;
